import React, {
    ChangeEvent,
    ChangeEventHandler,
    CSSProperties,
    FC,
    KeyboardEvent,
    ReactElement,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useKeyboardFocusHighlighting } from '../../hooks/useKeyboardFocusHighlighting';
import { useUuid } from '../../hooks/uuid';
import { getHeightOfSingleTextLine } from '../../utils/calculate';
import {
    StyledCheckbox,
    StyledCheckboxBox,
    StyledCheckboxBoxWrapper,
    StyledCheckboxInput,
    StyledCheckboxLabel,
} from './Checkbox.styles';

type CheckboxColors = {
    /**
     * Background color for the checked state.
     */
    checkedBackgroundColor?: CSSProperties['backgroundColor'];
    /**
     * Background color for the unchecked state.
     */
    uncheckedBackgroundColor?: CSSProperties['backgroundColor'];

    /**
     * Border color for the checkbox or switch indicator.
     */
    borderColor?: CSSProperties['borderColor'];
};

export type CheckboxProps = {
    /**
     * Text for checkbox or switch
     */
    children?: ReactElement | string;
    /**
     * Indicates whether the checkbox or switch is selected
     */
    isChecked?: boolean;
    /**
     * Disables the checkbox or switch so it cannot be toggled
     */
    isDisabled?: boolean;
    /**
     * Classname for the label
     */
    labelClassName?: string;
    /**
     * Function to be executed if the checked value changes
     */
    onChange?: ChangeEventHandler<HTMLInputElement>;
    /**
     * Whether the label should change the state of the checkbox
     */
    shouldChangeOnLabelClick?: boolean;
    /**
     * Changes the design to use switch instead of checkbox
     */
    shouldShowAsSwitch?: boolean;
    /**
     * Border radius for the checkbox or switch indicator.
     */
    borderRadius?: CSSProperties['borderRadius'];
    colors?: CheckboxColors;
    /**
     * Whether the Checkbox should be displayed centered to the label or at the top
     */
    shouldShowCentered?: boolean;
    /**
     * Enables keyboard-only focus highlighting.
     */
    shouldEnableKeyboardHighlighting?: boolean;
};

const Checkbox: FC<CheckboxProps> = ({
    children,
    isChecked,
    isDisabled,
    labelClassName,
    onChange,
    shouldShowAsSwitch,
    borderRadius,
    colors,
    shouldShowCentered = false,
    shouldChangeOnLabelClick = true,
    shouldEnableKeyboardHighlighting,
}) => {
    const [isActive, setIsActive] = useState(isChecked ?? false);
    const [checkboxTop, setCheckboxTop] = useState<number | undefined>(undefined);

    const checkboxBoxRef = useRef<HTMLLabelElement>(null);
    const checkboxRootRef = useRef<HTMLDivElement>(null);

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setIsActive(event.target.checked);

            if (typeof onChange === 'function') {
                onChange(event);
            }
        },
        [onChange],
    );

    const uuid = useUuid();

    useEffect(() => {
        if (checkboxRootRef.current && !shouldShowCentered) {
            const singleLineHeight = getHeightOfSingleTextLine({
                container: checkboxRootRef.current,
            });

            const boxHeight = checkboxBoxRef.current?.getBoundingClientRect().height ?? 0;

            setCheckboxTop((singleLineHeight - boxHeight) / 2);
        }
    }, [shouldShowCentered]);

    const shouldShowKeyboardHighlighting = useKeyboardFocusHighlighting(
        shouldEnableKeyboardHighlighting && !isDisabled,
    );

    return (
        <StyledCheckbox ref={checkboxRootRef}>
            <StyledCheckboxInput
                $shouldShowKeyboardHighlighting={shouldShowKeyboardHighlighting}
                checked={isChecked}
                disabled={isDisabled}
                id={uuid}
                onChange={handleChange}
                type="checkbox"
            />

            <StyledCheckboxBoxWrapper
                $shouldShowAsSwitch={shouldShowAsSwitch}
                style={{
                    top: shouldShowCentered ? '50%' : checkboxTop,
                    transform: shouldShowCentered ? 'translateY(-50%)' : undefined,
                }}
            >
                <StyledCheckboxBox
                    htmlFor={uuid}
                    ref={checkboxBoxRef}
                    $isChecked={isChecked ?? isActive}
                    $isDisabled={isDisabled}
                    $shouldShowAsSwitch={shouldShowAsSwitch}
                    $checkedBackgroundColor={colors?.checkedBackgroundColor}
                    $uncheckedBackgroundColor={colors?.uncheckedBackgroundColor}
                    $borderRadius={borderRadius}
                    $borderColor={colors?.borderColor}
                />
            </StyledCheckboxBoxWrapper>
            <StyledCheckboxLabel
                className={labelClassName}
                $isDisabled={isDisabled}
                $shouldChangeOnLabelClick={shouldChangeOnLabelClick}
                $shouldShowAsSwitch={shouldShowAsSwitch}
                htmlFor={shouldChangeOnLabelClick ? uuid : undefined}
            >
                {children}
            </StyledCheckboxLabel>
        </StyledCheckbox>
    );
};

Checkbox.displayName = 'Checkbox';

export default Checkbox;
